import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { helpAPI } from '../../../services/api';
import { ArrowLeft, ArrowRight, Search, Book, ChevronRight, ChevronDown, Menu, Home, HelpCircle, LifeBuoy, LayoutDashboard, LogOut } from 'lucide-react';
import { navigation } from '../../../config/navigationConfig';
import { useAuth } from '../../../contexts/AuthContext';
import BrandLogo from '../../../components/BrandLogo';
import toast from 'react-hot-toast';

const MenuGroup = ({ label, icon: Icon, items = [] }) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef(null)
  const closeTimeoutRef = useRef(null)

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target)) setIsOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    }
  }, [])

  const visibleItems = items.filter((i) => !i.hidden)
  const cancelClose = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = null
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 180)
  }

  return (
    <div
      ref={ref}
      className="relative h-full flex items-center"
      onMouseEnter={() => {
        cancelClose()
        setIsOpen(true)
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={() => setIsOpen((s) => !s)}
        className={`flex items-center gap-2 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-colors ${
          isOpen ? 'bg-gray-700 text-white' : 'text-white hover:bg-gray-700 hover:text-white'
        }`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {Icon ? <Icon className="w-4 h-4" /> : null}
        <span className="whitespace-nowrap">{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && visibleItems.length > 0 ? (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-xl rounded-lg z-50 min-w-[220px] py-1"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {visibleItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-700"
              onClick={() => setIsOpen(false)}
            >
              {item.icon ? <item.icon className="w-3.5 h-3.5 text-gray-400" /> : null}
              <span className="truncate">{item.label}</span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default function HelpLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout } = useAuth()

  // Helper to find icon from navigation config
  const getIconForTitle = (title) => {
    // Search recursively in navigation config
    const searchNav = (items) => {
      for (const item of items) {
        if (item.name === title) return item.icon;
        if (item.children) {
          const childIcon = searchNav(item.children);
          if (childIcon) return childIcon;
        }
      }
      return null;
    };
    return searchNav(navigation) || HelpCircle;
  };

  useEffect(() => {
    document.title = 'Help & Support - Sanglob Next-Gen ERP.';
    return () => {
      document.title = 'Sanglob Next-Gen ERP.';
    };
  }, []);

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (_) {
      window.scrollTo(0, 0)
    }
  }, [location.pathname])

  const { data: structure, isLoading } = useQuery({
    queryKey: ['help-structure'],
    queryFn: helpAPI.getStructure
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/help/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const backToErpHref = useMemo(() => {
    const qs = new URLSearchParams(location.search || '')
    const explicit = qs.get('returnTo')
    if (explicit) return explicit
    try {
      return sessionStorage.getItem('erp:returnTo') || '/dashboard'
    } catch (_) {
      return '/dashboard'
    }
  }, [location.search])

  const docsTree = useMemo(() => {
    return Array.isArray(structure?.data?.data) ? structure.data.data : []
  }, [structure])

  const findHelpPathBySlug = useMemo(() => {
    const walk = (items, slug, chain = []) => {
      for (const item of items) {
        const nextChain = [...chain, item]
        if (String(item.slug || '') === String(slug || '')) return nextChain
        if (Array.isArray(item.children) && item.children.length) {
          const found = walk(item.children, slug, nextChain)
          if (found) return found
        }
      }
      return null
    }
    return (slug) => walk(docsTree, slug, [])
  }, [docsTree])

  const breadcrumbs = useMemo(() => {
    const parts = String(location.pathname || '').split('/').filter(Boolean)
    const base = [{ label: 'Help Home', to: '/help' }]
    if (parts.length <= 1) return base

    const section = parts[1]
    if (section === 'search') return [...base, { label: 'Search', to: location.pathname + (location.search || '') }]

    const chain = findHelpPathBySlug(section)
    if (chain && chain.length) {
      const mapped = chain.map((n) => ({ label: String(n.title || n.slug || 'Article'), to: `/help/${n.slug}` }))
      return [...base, ...mapped]
    }

    return [...base, { label: 'Article', to: location.pathname }]
  }, [findHelpPathBySlug, location.pathname, location.search])

  const TreeItem = ({ item, depth = 0 }) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasChildren = item.children && item.children.length > 0;
    const isActive = location.pathname === `/help/${item.slug}`;
    const Icon = getIconForTitle(item.title);

    // Auto-expand if active child
    useEffect(() => {
        if (hasChildren && !isOpen) {
            const hasActiveChild = (children) => {
                return children.some(child => 
                    location.pathname === `/help/${child.slug}` || 
                    (child.children && hasActiveChild(child.children))
                );
            };
            if (hasActiveChild(item.children)) {
                setIsOpen(true);
            }
        }
    }, [location.pathname, hasChildren]);

    return (
      <div className="select-none">
        <div 
          className={`
            flex items-center px-3 py-2 text-sm cursor-pointer rounded-md transition-colors
            ${isActive ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}
          `}
          style={{ paddingLeft: `${depth * 12 + 12}px` }}
          onClick={() => {
            if (hasChildren) setIsOpen(!isOpen);
            if (!hasChildren || (hasChildren && !isOpen)) {
                navigate(`/help/${item.slug}`);
            }
          }}
        >
          {hasChildren ? (
            <span className="mr-1 text-gray-400">
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          ) : (
            <span className="w-5" />
          )}
          
          <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
          
          <span className="truncate">{item.title}</span>
        </div>
        {isOpen && hasChildren && (
          <div>
            {item.children.map(child => (
              <TreeItem key={child.id} item={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col h-screen overflow-hidden">
      {/* Top Header */}
      <header className="bg-[#1e293b] text-white px-4 h-14 flex items-center justify-between shrink-0 shadow-md sticky top-0 z-30">
        <div className="flex items-center gap-4 lg:gap-8 min-w-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-1 text-gray-300 hover:text-white"
            type="button"
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link to="/help" className="flex items-center gap-3 group shrink-0 pr-3 border-r border-slate-700" title="Global AI ERP Help">
            <BrandLogo 
              className="h-5.5 sm:h-6 group-hover:scale-105 transition-transform" 
              dark={true}
              bySanglobClassName="text-[7.5px] text-slate-400 tracking-wider"
            />
          </Link>

          <nav className="hidden md:flex items-center space-x-1 h-14 overflow-visible whitespace-nowrap pr-2" aria-label="Help Navigation">
            <Link
              to="/help"
              className="flex items-center gap-2 px-2.5 py-1.5 text-[13px] font-medium rounded-md text-white hover:bg-gray-700 hover:text-white"
            >
              <LayoutDashboard className="w-4 h-4" />
              Help Home
            </Link>

            <MenuGroup
              label="Knowledge"
              icon={Book}
              items={[
                { to: '/help', label: 'Browse Articles', icon: Book },
                { to: '/help/search', label: 'Search', icon: Search }
              ]}
            />

            <MenuGroup
              label="Support"
              icon={LifeBuoy}
              items={[
                { to: '/tickets/new', label: 'Submit Ticket', icon: LifeBuoy },
                { to: '/tickets/my', label: 'My Tickets', icon: LifeBuoy },
                { to: '/tickets/dashboard', label: 'Ticket Dashboard', icon: LayoutDashboard }
              ]}
            />
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
          <form onSubmit={handleSearch} className="relative hidden md:block w-[420px] max-w-[42vw]">
            <input
              type="text"
              placeholder="Search help..."
              className="w-full h-9 pl-10 pr-3 bg-gray-700/40 border border-gray-600/50 rounded-md text-sm text-white placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:border-blue-400/60"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-300" />
          </form>

          <a
            href={backToErpHref}
            className="hidden sm:inline-flex items-center gap-2 h-9 px-3 text-sm text-gray-200 hover:text-white rounded-md hover:bg-gray-700/50 transition-colors"
          >
            <Home className="w-4 h-4" />
            Back to ERP
          </a>
          <button
            type="button"
            onClick={logout}
            className="p-2 text-gray-200 hover:text-white hover:bg-gray-700/50 rounded-md transition-colors"
            title="Sign Out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden w-full">
        {/* Sidebar - Navigation Tree */}
        <aside 
          className={`
            w-80 bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0
            ${sidebarOpen ? 'block' : 'hidden'} 
            lg:block absolute lg:relative z-20 h-full transition-all duration-300
          `}
        >
          <div className="p-4">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Documentation</h2>
            {isLoading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-4 bg-gray-100 rounded w-3/4"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {structure?.data?.data?.map(item => (
                  <TreeItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-white">
          <div className="border-b border-gray-200 bg-white">
            <div className="w-full px-4 sm:px-6 lg:px-8 2xl:px-10 h-12 flex items-center justify-between gap-3">
              <div className="flex items-center min-w-0 text-sm text-gray-600">
                {breadcrumbs.map((c, idx) => {
                  const isLast = idx === breadcrumbs.length - 1
                  return (
                    <div key={`${c.to}-${c.label}`} className="flex items-center min-w-0">
                      {idx > 0 ? <ChevronRight className="w-4 h-4 text-gray-300 mx-2 shrink-0" /> : null}
                      {isLast ? (
                        <span className="font-medium text-gray-900 truncate">{c.label}</span>
                      ) : (
                        <Link to={c.to} className="hover:text-indigo-700 truncate">
                          {c.label}
                        </Link>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="h-9 inline-flex items-center gap-2 px-3 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 text-gray-600" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => navigate(1)}
                  className="h-9 inline-flex items-center gap-2 px-3 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50"
                >
                  Next
                  <ArrowRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
